import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { IGateway, IMetricasGateway, IMetricasGateways } from 'modelos/src';
import { Subscription } from 'rxjs';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import { GatewayService } from '../../../../../auxiliares/servicios/http/gateways.service';
import { CrearEditarGatewayComponent } from '../crear-editar-gateway/crear-editar-gateway.component';

@Component({
  selector: 'app-detalles-gateway',
  templateUrl: './detalles.component.html',
  styleUrls: [
    '../../../../../componentes/detalle/detalle-pagina.scss',
    './detalles.component.scss',
  ],
  standalone: false,
})
export class DetallesGatewayComponent implements OnInit, OnDestroy {
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private lista2 = inject(Lista2Service);
  private gatewayService = inject(GatewayService);
  helper = inject(HelperService);
  private formularios = inject(FormularioDialogService);

  // El mismo permiso que pide el listado para editar
  public puedeEditar = this.helper.tienePermiso(
    'Administración - Editar gateways',
  );

  public loading = true;
  public selectedTab = 0;
  public gateway?: IGateway;
  /** Métricas de 15 días y airtime de 24 h: el resumen de Info y Métricas. */
  public metricas?: IMetricasGateways;
  public airtime?: IMetricasGateway[];
  private gateway$?: Subscription;
  private params$?: Subscription;
  private queryParams$?: Subscription;
  private idGateway = '';

  /** Las pestañas en orden, por nombre. Tiene que coincidir con la plantilla. */
  private pestanas(): string[] {
    return ['info', 'metricas'];
  }

  public tabChanged(event: MatTabChangeEvent): void {
    this.selectedTab = event.index;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { tab: this.pestanas()[event.index] ?? event.index },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** `tab` por nombre, o por índice (links viejos). */
  private indicePestana(tab: string): number | null {
    if (/^\d+$/.test(tab)) return parseInt(tab);
    const i = this.pestanas().indexOf(tab);
    return i >= 0 ? i : null;
  }

  private async listarGateway(): Promise<void> {
    return new Promise((resolve) => {
      this.gateway$?.unsubscribe();
      // Si el gateway no existe (se borró, no hay acceso) el getById falla:
      // sin el error la promesa no se resolvía y la página quedaba en blanco.
      this.gateway$ = this.lista2.gateway(this.idGateway).subscribe({
        next: (data: IGateway) => {
          this.gateway = data;
          resolve();
        },
        error: () => {
          this.gateway = undefined;
          resolve();
        },
      });
    });
  }

  /** 15 días por día, como el Network Server. */
  private async listarMetricas(gateway: IGateway): Promise<void> {
    const hasta = new Date();
    const desde = new Date();
    desde.setDate(desde.getDate() - 15);
    try {
      this.metricas = await this.gatewayService.getMetricas(
        gateway.gatewayEui,
        desde.toISOString(),
        hasta.toISOString(),
        'DAY',
      );
    } catch (error) {
      console.error('Error al traer métricas del gateway:', error);
    }
  }

  private async listarAirtime(gateway: IGateway): Promise<void> {
    const hasta = new Date();
    const desde = new Date();
    desde.setHours(desde.getHours() - 24);
    try {
      const res = await this.gatewayService.getAirtime(
        gateway._id,
        desde.toISOString(),
        hasta.toISOString(),
      );
      this.airtime = res.datos ?? [];
    } catch (error) {
      console.error('Error al traer airtime del gateway:', error);
    }
  }

  // Edición en modal; al guardar se vuelve a leer el gateway (Info lo sigue
  // con ngOnChanges)
  public editar(): void {
    if (!this.gateway) return;
    this.formularios
      .abrir(CrearEditarGatewayComponent, {
        tamano: 'mediano',
        data: { edit: this.gateway },
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) this.listarGateway();
      });
  }

  /**
   * Carga el gateway del id de la ruta. Se llama cada vez que cambia el id:
   * Angular reutiliza el componente al ir de un gateway a otro.
   */
  private async cargar(id: string | null): Promise<void> {
    this.loading = true;
    this.gateway = undefined;
    this.metricas = undefined;
    this.airtime = undefined;
    this.idGateway = id || '';
    await this.listarGateway();
    this.loading = false;
    // Las métricas llegan después: la página no las espera
    const gateway = this.gateway;
    if (gateway) {
      void this.listarMetricas(gateway);
      void this.listarAirtime(gateway);
    }
  }

  ngOnInit(): void {
    this.params$ = this.activatedRoute.paramMap.subscribe((params) => {
      void this.cargar(params.get('id'));
    });
    this.queryParams$ = this.activatedRoute.queryParams.subscribe((params) => {
      const indice = params['tab'] ? this.indicePestana(params['tab']) : null;
      if (indice !== null) this.selectedTab = indice;
    });
  }

  ngOnDestroy(): void {
    this.gateway$?.unsubscribe();
    this.params$?.unsubscribe();
    this.queryParams$?.unsubscribe();
  }
}
